import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FaPrint, FaFileAlt, FaImage, 
  FaListUl, FaCheckCircle, FaChevronRight,
  FaTimes, FaQrcode
} from 'react-icons/fa';

export default function TallyQuotationReview() {
  const { id } = useParams();
  const [showFormatSelection, setShowFormatSelection] = useState(true);
  const [printType, setPrintType] = useState('full');
  const [loading, setLoading] = useState(true);
  const [quotation, setQuotation] = useState(null);
  const [printNetAvailable, setPrintNetAvailable] = useState(false);
  const [printAvailability, setPrintAvailability] = useState(false);
  const [printClosingBalance, setPrintClosingBalance] = useState(false);
  const [printCoverageArea, setPrintCoverageArea] = useState(true);
  const [printRatePerSqft, setPrintRatePerSqft] = useState(true);
  const [printBrand, setPrintBrand] = useState(true);
  const [pricingInSqft, setPricingInSqft] = useState(false);
  const [qrPreference, setQrPreference] = useState({
    basic: true,
    medium: true,
    full: true,
    discount: true,
    project: true,
  });

  const printGroups = [
    {
      baseId: 'basic',
      title: 'Basic Print',
      description: 'Simple quotation without brand names, MRP, and product images',
      icon: <FaListUl size={24} />,
      color: 'red',
      shortcut: '1'
    },
    {
      baseId: 'medium',
      title: 'Standard Print',
      description: 'Includes product images but excludes brand names and MRP',
      icon: <FaImage size={24} />,
      color: 'yellow',
      shortcut: '2'
    },
    {
      baseId: 'full',
      title: 'Detailed Print',
      description: 'Complete quotation with brands, MRP, and images',
      icon: <FaFileAlt size={24} />,
      color: 'orange',
      shortcut: '3'
    },
    {
      baseId: 'discount',
      title: 'Discount Print',
      description: 'Detailed quotation with discount column',
      icon: <FaFileAlt size={24} />,
      color: 'rose',
      shortcut: '4'
    },
    {
      baseId: 'project',
      title: 'Project Print',
      description: 'Project quotation with GST inclusive prices',
      icon: <FaCheckCircle size={24} />,
      color: 'gray',
      shortcut: '5'
    }
  ];

  const getTypeId = useCallback((baseId) => {
    return qrPreference[baseId] ? baseId : `${baseId}-no-qr`;
  }, [qrPreference]);

  useEffect(() => {
    const fetchQuotationData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/tally-quotation/${id}`);
        if (response.data) {
          setQuotation(response.data);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    
    fetchQuotationData();
  }, [id]);

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!showFormatSelection) return;
      
      const key = event.key;
      const selectedGroup = printGroups.find(g => g.shortcut === key);
      
      if (selectedGroup) {
        event.preventDefault();
        handleTypeSelect(getTypeId(selectedGroup.baseId));
      }
      
      // ESC key to toggle format selection
      if (key === 'Escape') {
        setShowFormatSelection(!showFormatSelection);
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [showFormatSelection, qrPreference, getTypeId]);

  const generatePdfUrl = (type) => {
    const baseType = type.replace('-no-qr', '');
    const printWithQR = !type.endsWith('-no-qr');
    let dataUrl = `${process.env.REACT_APP_API_BASE_URL}/tally-quotation/print/${id}?printType=${baseType}&printWithQR=${printWithQR}`;
    if (printNetAvailable) dataUrl += '&printNetAvailable=true';
    if (printAvailability) dataUrl += '&printAvailability=true';
    if (printClosingBalance) dataUrl += '&printClosingBalance=true';
    if (!printCoverageArea) dataUrl += '&printCoverageArea=false';
    if (!printRatePerSqft) dataUrl += '&printRatePerSqft=false';
    if (!printBrand) dataUrl += '&printBrand=false';
    if (pricingInSqft) dataUrl += '&pricingInSqft=true';
    return `https://rlugroup.in/pdf/convert?fileName=rlugroup-tally-quotation.hbs&url=${encodeURIComponent(dataUrl)}`;
  };

  const handleTypeSelect = (type) => {
    setPrintType(type);
    setShowFormatSelection(false);
  };

  const FormatSelectionModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-11/12 md:w-3/4 lg:w-2/3 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold flex items-center">
            <FaPrint className="mr-2 text-gray-600" />
            Select Print Format
            <span className="ml-3 text-sm font-normal text-gray-500">(Use number keys 1-5 or ESC)</span>
          </h3>
          <button 
            className="text-gray-500 hover:text-gray-700 focus:outline-none transition-all duration-200 hover:scale-110 active:scale-95"
            onClick={() => setShowFormatSelection(false)}
          >
            <FaTimes size={24} />
          </button>
        </div>
        
        {!loading && quotation && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-lg font-medium mb-3">Print Options</h4>
            <div className="flex flex-wrap gap-6 mb-4">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={printNetAvailable}
                  onChange={(e) => setPrintNetAvailable(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm font-medium text-gray-700">Print Net Available</span>
                <span className="text-xs text-gray-400">(Show stock quantity per item)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={printCoverageArea}
                  onChange={(e) => setPrintCoverageArea(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm font-medium text-gray-700">Print Coverage Area</span>
                <span className="text-xs text-gray-400">(Show coverage area column)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={printRatePerSqft}
                  onChange={(e) => setPrintRatePerSqft(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="text-sm font-medium text-gray-700">Print Rate per Sqft/Sq.m</span>
                <span className="text-xs text-gray-400">(Show rate per unit area in amount)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={printAvailability}
                  onChange={(e) => setPrintAvailability(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-sm font-medium text-gray-700">Print Availability</span>
                <span className="text-xs text-gray-400">(Show In Stock / Out of Stock)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={printClosingBalance}
                  onChange={(e) => setPrintClosingBalance(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                />
                <span className="text-sm font-medium text-gray-700">Print Closing Balance</span>
                <span className="text-xs text-gray-400">(Show stock balance per item)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={printBrand}
                  onChange={(e) => setPrintBrand(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Print Brand</span>
                <span className="text-xs text-gray-400">(Show the brand column)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={pricingInSqft}
                  onChange={(e) => setPricingInSqft(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm font-medium text-gray-700">Pricing in Sq.ft/Sq.m</span>
                <span className="text-xs text-gray-400">(Show rate & qty per area unit)</span>
              </label>
            </div>
            <h4 className="text-lg font-medium mb-3">Quotation Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-white rounded shadow-sm">
                <div className="text-xs text-gray-500 mb-1">Date</div>
                <div className="font-medium">
                  {new Date(quotation.createdAt).toLocaleDateString()}
                </div>
              </div>
              <div className="p-3 bg-white rounded shadow-sm">
                <div className="text-xs text-gray-500 mb-1">Recipient</div>
                <div className="font-medium">
                  {quotation.recipient?.name || quotation.recipient?.businessName}
                </div>
              </div>
              <div className="p-3 bg-white rounded shadow-sm">
                <div className="text-xs text-gray-500 mb-1">Sections</div>
                <div className="font-medium">{quotation.sections?.length || 0}</div>
              </div>
              <div className="p-3 bg-white rounded shadow-sm">
                <div className="text-xs text-gray-500 mb-1">Total Amount</div>
                <div className="font-medium text-green-600">
                  ₹{quotation.totalAmount?.toLocaleString('en-IN') || '0'}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
          {printGroups.map((group) => {
            const typeId = getTypeId(group.baseId);
            const isSelected = printType === typeId;
            const withQR = qrPreference[group.baseId];
            return (
              <div 
                key={group.baseId}
                className={`bg-gradient-to-br from-${group.color}-50 to-${group.color}-100 rounded-lg shadow-md transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl hover:scale-105 cursor-pointer border-2 group ${
                  isSelected ? `border-${group.color}-500 bg-gradient-to-br from-${group.color}-100 to-${group.color}-200` : `border-${group.color}-200 hover:border-${group.color}-400`
                } hover:bg-gradient-to-br hover:from-${group.color}-100 hover:to-${group.color}-200`}
                onClick={() => handleTypeSelect(typeId)}
              >
                <div className="p-6 text-center transition-all duration-300 group-hover:transform group-hover:scale-105">
                  <div className={`mx-auto w-16 h-16 rounded-full bg-${group.color}-200 border-2 border-${group.color}-300 flex items-center justify-center mb-4 relative transition-all duration-300 group-hover:scale-110 group-hover:bg-${group.color}-300 group-hover:border-${group.color}-400`}>
                    <div className={`text-${group.color}-700 transition-all duration-300 group-hover:text-${group.color}-800`}>
                      {group.icon}
                    </div>
                  </div>
                  <h4 className={`text-lg font-medium mb-2 transition-all duration-300 text-${group.color}-800 group-hover:text-${group.color}-900`}>
                    {group.title}
                    <span className={`ml-2 inline-flex items-center justify-center w-6 h-6 bg-${group.color}-300 text-${group.color}-800 text-xs rounded-full font-bold group-hover:bg-${group.color}-400 group-hover:text-${group.color}-900`}>
                      {group.shortcut}
                    </span>
                  </h4>
                  <p className={`text-${group.color}-600 text-sm mb-3 transition-all duration-300 group-hover:text-${group.color}-700`}>{group.description}</p>
                  
                  {/* QR Toggle */}
                  <div className="flex items-center justify-center mb-4" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setQrPreference(prev => ({ ...prev, [group.baseId]: !prev[group.baseId] }));
                      }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 border ${
                        withQR
                          ? 'bg-indigo-100 text-indigo-700 border-indigo-300 hover:bg-indigo-200'
                          : 'bg-gray-100 text-gray-500 border-gray-300 hover:bg-gray-200'
                      }`}
                      title={withQR ? 'Click to remove QR code' : 'Click to add QR code'}
                    >
                      <FaQrcode className={withQR ? 'text-indigo-600' : 'text-gray-400'} />
                      {withQR ? 'With QR' : 'No QR'}
                    </button>
                  </div>

                  <button 
                    className={`w-full py-2 px-4 rounded text-sm font-medium flex items-center justify-center transition-all duration-300 transform hover:scale-105 active:scale-95 ${
                      isSelected 
                        ? `bg-${group.color}-600 text-white shadow-lg hover:bg-${group.color}-700 hover:shadow-xl border-2 border-${group.color}-700` 
                        : `bg-${group.color}-200 text-${group.color}-800 border-2 border-${group.color}-400 hover:bg-${group.color}-300 hover:border-${group.color}-500 hover:text-${group.color}-900 hover:shadow-md`
                    }`}
                  >
                    {isSelected ? (
                      <>
                        <FaCheckCircle className="mr-2" /> Selected
                      </>
                    ) : (
                      <>
                        Select <FaChevronRight className="ml-2" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Format Selection Modal - Shows by default */}
      {showFormatSelection && <FormatSelectionModal />}

      {/* PDF Viewer */}
      <div className={`h-screen w-full ${showFormatSelection ? 'hidden' : 'block'}`}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <object
            data={generatePdfUrl(printType)}
            type="application/pdf"
            className="w-full h-full"
            aria-label="Tally Quotation PDF"
          >
            <div className="text-center p-8">
              <p className="mb-4">Your browser doesn't support embedded PDFs.</p>
              <a 
                href={generatePdfUrl(printType)} 
                target="_blank" 
                rel="noreferrer"
              >
                Download the PDF
              </a>
            </div>
          </object>
        )}
      </div>
    </>
  );
}